<template>
  <div class="realestates-page">

    <!--Loading hostaway export spinner-->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner">
        <b-spinner></b-spinner>
      </div>
    </div>

    <!-- Hero banner -->
    <page-hero
        class="mb-1"
        :title="msg('Real estates')"
        :subtitle="msg('Manage properties, apartments and rooms')"
        variant="realestate"
        :stats="[{ icon: 'HomeIcon', value: page.total, label: msg('total') }]"
    >
      <template #illustration>
        <svg viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M50 15L15 45v60h70V45L50 15z" fill="rgba(255,255,255,.2)" stroke="rgba(255,255,255,.3)" stroke-width="1.5"/>
          <rect x="42" y="55" width="16" height="25" rx="2" fill="rgba(255,255,255,.35)"/>
          <circle cx="75" cy="30" r="8" fill="rgba(255,255,255,.1)"/>
          <circle cx="12" cy="85" r="10" fill="rgba(255,255,255,.06)"/>
        </svg>
      </template>
    </page-hero>

    <!--Breadcrumb button-->
    <breadcrumb-buttons hide-settings>
      <template #default>
        <div class="flex-container">
          <b-button id="import-real-estates-button" variant="primary" class="btn-icon" v-b-tooltip
                    v-bind:title="msg('importrealestates')" @click="dialogs.importrealestates=true">
            <i class="fa-solid fa-file-arrow-up"></i>
          </b-button>
          <b-button id="import-from-hostaway-button" variant="primary" class="btn-icon" v-b-tooltip
                    v-bind:title="msg('Import from External channel manager')">
            <i class="fa-solid fa-cloud-arrow-down"></i>
          </b-button>
          <!--          <b-button id="import-from-hostaway-button" variant="primary" class="btn-icon" v-b-tooltip v-bind:title="$t('channelManager')" @click="$refs.channelManagerModal.show()">
                      <i class="fa-solid fa-plug"></i>
                    </b-button>-->
          <b-button id="importPictures" variant="primary" class="btn-icon" v-b-tooltip
                    v-bind:title="$t('Upload images')" @click="openImageModal">
            <i class="fa-solid fa-image"></i>
          </b-button>
        </div>

      </template>
    </breadcrumb-buttons>

    <!--Filters-->
    <div class="re-filterpanel mb-2">
      <!-- Top bar: search + active/inactive + summary -->
      <div class="re-filterpanel__bar">
        <div class="re-search">
          <feather-icon icon="SearchIcon" size="17" class="re-search__icon"/>
          <b-form-input
              v-model="filter.like.name"
              class="re-search__input"
              :placeholder="msg('Search by name')"
          />
          <feather-icon
              v-if="filter.like.name"
              icon="XIcon" size="16" class="re-search__clear"
              @click="filter.like.name = ''"
          />
        </div>

        <!-- Active / Inactive segmented control (wired to filter.eq.advertise) -->
        <div class="re-seg" role="group" :aria-label="msg('Status')">
          <button type="button" class="re-seg__btn"
                  :class="{'is-active': filter.eq.advertise == null}"
                  @click="setAdvertise(null)">
            {{ msg('All') }}
          </button>
          <button type="button" class="re-seg__btn re-seg__btn--on"
                  :class="{'is-active': filter.eq.advertise === true}"
                  @click="setAdvertise(true)">
            <span class="re-seg__dot"></span>{{ msg('Active') }}
          </button>
          <button type="button" class="re-seg__btn re-seg__btn--off"
                  :class="{'is-active': filter.eq.advertise === false}"
                  @click="setAdvertise(false)">
            <span class="re-seg__dot"></span>{{ msg('Inactive') }}
          </button>
        </div>

        <div class="re-filterpanel__summary" v-b-tooltip.hover :title="msg('Total')">
          <feather-icon icon="HomeIcon" size="15"/>
          <b>{{ page.total }}</b>&nbsp;{{ msg('properties') }}
        </div>
      </div>

      <!-- Quick filters -->
      <div class="re-filterpanel__quick">
        <div class="re-qf">
          <label class="re-qf__label">{{ msg('Estate agents') }}</label>
          <v-select
              multiple append-to-body
              :dir="isRTL ? 'rtl' : 'ltr'"
              :clearable="true"
              v-model="filter.in.userIds"
              :options="allAgents"
              class="re-qf__sel"
              label="name"
              :reduce="(branch) => branch.id"
              :placeholder="msg('All')"
          />
        </div>
        <div class="re-qf">
          <label class="re-qf__label">{{ msg('Buy/Rent') }}</label>
          <v-select
              append-to-body
              :dir="isRTL ? 'rtl' : 'ltr'"
              :clearable="true"
              v-model="filter.eq.marketingType"
              :options="[{value:{purchase:false,rent:true},label:msg('Rent')},{value:{purchase:true,rent:false},label:msg('Purchase')}]"
              class="re-qf__sel"
              label="label"
              :reduce="(branch) => branch.value"
              :placeholder="msg('All')"
          />
        </div>
        <div class="re-qf">
          <label class="re-qf__label">{{ msg('Usage') }}</label>
          <v-select
              append-to-body
              :dir="isRTL ? 'rtl' : 'ltr'"
              :clearable="true"
              v-model="filter.eq.usageType"
              :options="[
                  {value:{business:false,investment:false,living:true,waz:false},label:msg('Living')},
                  {value:{business:true,investment:false,living:false,waz:false},label:msg('Business')},
                  {value:{business:false,investment:true,living:false,waz:false},label:msg('Investment')}]"
              class="re-qf__sel"
              label="label"
              :reduce="(branch) => branch.value"
              :placeholder="msg('All')"
          />
        </div>
        <div class="re-qf">
          <label class="re-qf__label">{{ msg('Nuki status') }}</label>
          <v-select
              multiple append-to-body
              :dir="isRTL ? 'rtl' : 'ltr'"
              :clearable="true"
              v-model="filter.in.nukiStatus"
              :options="[
                  {value:null,label:msg('NODEVICE')},
                  {value:'INSTALLED',label:msg('INSTALLED')},
                  {value:'TOINSTALL',label:msg('TOINSTALL')},
                  {value:'FAULT',label:msg('FAULT')},
              ]"
              class="re-qf__sel"
              label="label"
              :reduce="(branch) => branch.value"
              :placeholder="msg('All')"
          />
        </div>
        <div class="re-qf">
          <label class="re-qf__label">{{ msg('Featured status') }}</label>
          <v-select
              multiple append-to-body
              :dir="isRTL ? 'rtl' : 'ltr'"
              :clearable="true"
              v-model="filter.eq.featureStatus"
              :options="[
                  {value:{propertyPage:true},label:msg('PROPERTYPAGE')},
                  {value:{searchPage:true},label:msg('SEARCHPAGE')},
                  {value:{homePage:true},label:msg('HOMEPAGE')},
              ]"
              class="re-qf__sel"
              label="label"
              :reduce="(branch) => branch.value"
              :placeholder="msg('All')"
          />
        </div>

        <div class="re-qf re-qf--actions">
          <b-button class="re-morebtn" :class="{'is-open': isCollapsed}" @click="toggleCollapse">
            <feather-icon icon="SlidersIcon" size="14" class="mr-25"/>
            {{ isCollapsed ? msg('Fewer filters') : msg('More filters') }}
            <b-badge v-if="advancedFilterCount > 0" class="re-morebtn__count">{{ advancedFilterCount }}</b-badge>
            <feather-icon :icon="isCollapsed ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" class="ml-25"/>
          </b-button>
          <b-button v-if="activeFilterCount > 0" class="re-clearbtn" @click="resetFilters">
            <feather-icon icon="XIcon" size="14" class="mr-25"/>{{ msg('Clear all') }}
            <b-badge class="re-clearbtn__count">{{ activeFilterCount }}</b-badge>
          </b-button>
        </div>
      </div>

      <!-- Advanced filters (collapsible) -->
      <b-collapse v-model="isCollapsed">
        <div class="re-filterpanel__adv">
          <div class="re-adv-head">
            <feather-icon icon="FilterIcon" size="14"/>
            <span>{{ msg('Advanced filters') }}</span>
          </div>
          <div class="re-adv-grid">
            <div class="re-field">
              <label>{{ msg("Id") }}</label>
              <b-form-input v-model="filter.like.id"/>
            </div>
            <div class="re-field">
              <label>{{ msg("Address") }}</label>
              <b-form-input v-model="filter.like.address"/>
            </div>
            <div class="re-field">
              <label>{{ msg("House number") }}</label>
              <b-form-input v-model="filter.like.house"/>
            </div>
            <div class="re-field">
              <label>{{ msg("Zip") }}</label>
              <b-form-input v-model="filter.like.zip"/>
            </div>
            <div class="re-field">
              <label>{{ msg("City") }}</label>
              <b-form-input v-model="filter.like.city"/>
            </div>
            <div class="re-field">
              <label>{{ msg("Rooms") }}</label>
              <b-form-input v-model="filter.eq.realEstateArea.rooms"/>
            </div>
            <div class="re-field">
              <label>{{ msg("Living space") }}</label>
              <b-form-input v-model="filter.eq.realEstateArea.livingSpace"/>
            </div>
            <div class="re-field">
              <label>{{ msg("Total space") }}</label>
              <b-form-input v-model="filter.eq.realEstateArea.totalSpace"/>
            </div>
            <div class="re-field">
              <label>{{ msg("HostawayId") }}</label>
              <b-form-input v-model="filter.eq.hostawayId"/>
            </div>
            <div class="re-field">
              <label>{{ msg('Room category') }}</label>
              <v-select
                  append-to-body
                  :dir="isRTL ? 'rtl' : 'ltr'"
                  v-model="filter.eq.imageCategory"
                  :options="[
                      {value:'B2',label:('Basic Zweizimmer')},
                      {value:'D2',label:('Deluxe Zweizimmer')},
                      {value:'B1',label:('Basic Einzimmer')},
                      {value:'D1',label:('Deluxe Einzimmer')},
                  ]"
                  label="label"
                  :reduce="(status) => status.value"
                  :placeholder="msg('All')"
              />
            </div>
          </div>

          <div class="re-adv-prices">
            <div class="re-priceblock">
              <span class="re-priceblock__title"><feather-icon icon="DollarSignIcon" size="13"/>{{ msg("RentTotal") }}</span>
              <div class="re-priceblock__row">
                <b-form-input :placeholder="msg('Min')" v-model="filter.gte.realEstatePrices.rentTotal"/>
                <span class="re-priceblock__dash">&ndash;</span>
                <b-form-input :placeholder="msg('Max')" v-model="filter.lte.realEstatePrices.rentTotal"/>
              </div>
            </div>
            <div class="re-priceblock">
              <span class="re-priceblock__title"><feather-icon icon="DollarSignIcon" size="13"/>{{ msg("RentNetto") }}</span>
              <div class="re-priceblock__row">
                <b-form-input :placeholder="msg('Min')" v-model="filter.gte.realEstatePrices.rentNetto"/>
                <span class="re-priceblock__dash">&ndash;</span>
                <b-form-input :placeholder="msg('Max')" v-model="filter.lte.realEstatePrices.rentNetto"/>
              </div>
            </div>
          </div>
        </div>
      </b-collapse>
    </div>

    <!--Data-->
    <b-card no-body class="table-card mb-0">
      <div class="card-header" style="overflow: hidden">
        <span class="card-title m-0">{{ msg('Real Estates') }}</span>
        <b-col>
          <div class="d-flex justify-content-end">
            <b-link style="color: inherit" @click="refresh()">
              <feather-icon icon="RefreshCcwIcon" size="16"/>
            </b-link>
          </div>
        </b-col>
      </div>
      <!--Card Content-->
      <b-card-body>
        <div class="re-list-toolbar">
          <div class="re-list-toolbar__left">
            <b-form-checkbox class="re-selectall" :checked="isAllRealEstatesSelected"
                             :indeterminate="!isAllRealEstatesSelected && selectedRealEstates.length>0"
                             @change="toggleAllRealEstates">
              {{ msg('Select all') }}
            </b-form-checkbox>
            <div class="re-list-toolbar__perpage">
              <label class="mb-0">{{ msg('Show') }}</label>
              <v-select
                  append-to-body
                  v-model="page.amount"
                  @option:selected="refresh"
                  :dir="isRTL ? 'rtl' : 'ltr'"
                  :options="isHotelMode ? [10, 25, 50, 100, 200] : [10, 25, 50]"
                  :clearable="false"
                  class="per-page-selector d-inline-block mx-50"
              />
              <label class="mb-0">{{ msg('entries') }}</label>
            </div>
          </div>
          <div class="re-list-toolbar__right">
            <b-dropdown class="re-sort-dd" variant="flat-secondary" right no-caret>
              <template #button-content>
                <feather-icon icon="SlidersIcon" size="15" class="mr-25"/>{{ sortLabel }}
              </template>
              <b-dropdown-item @click="setSort('desc')">{{ msg('Newest first') }}</b-dropdown-item>
              <b-dropdown-item @click="setSort('asc')">{{ msg('Oldest first') }}</b-dropdown-item>
            </b-dropdown>
          </div>
        </div>

        <!-- Bulk action bar -->
        <transition name="re-fade">
          <div v-if="selectedRealEstates.length > 0" class="re-bulkbar">
            <div class="re-bulkbar__count">
              <feather-icon icon="CheckSquareIcon" size="16"/>
              <b>{{ selectedRealEstates.length }}</b>&nbsp;{{ msg('selected') }}
            </div>
            <div class="re-bulkbar__actions">
              <b-button size="sm" class="re-bulk-btn re-bulk-btn--danger" @click="bulkDisableRealEsates">
                <feather-icon icon="SlashIcon" size="14" class="mr-25"/>{{ msg('Disable') }}
              </b-button>
              <b-dropdown size="sm" variant="warning" :text="msg('Advertise')" class="re-bulk-adv">
                <b-dropdown-item @click="bulkAdvertiseRealEstates('NONE')">Nicht Inserieren</b-dropdown-item>
                <b-dropdown-item @click="bulkAdvertiseRealEstates('NORMAL')">Normales Inserat (Kostenlos)</b-dropdown-item>
                <b-dropdown-item @click="bulkAdvertiseRealEstates('PROPERTYPAGE')">Immobilienseite</b-dropdown-item>
                <b-dropdown-item @click="bulkAdvertiseRealEstates('SEARCHPAGE')">Suchseite</b-dropdown-item>
                <b-dropdown-item @click="bulkAdvertiseRealEstates('HOMEPAGE')">Startseite</b-dropdown-item>
              </b-dropdown>
              <b-button size="sm" class="re-bulk-btn re-bulk-btn--ghost" @click="toggleAllRealEstates(false)">
                {{ msg('Clear') }}
              </b-button>
            </div>
          </div>
        </transition>
        <!-- Property list (rich cards) -->
        <div v-if="loading" class="text-center text-primary my-3">
          <b-spinner class="align-middle mx-1"></b-spinner>
          <strong>{{ msg('Loading') }}...</strong>
        </div>

        <div v-else class="re-plist">
          <div v-for="group in displayGroups" :key="group.id" class="re-group">
            <!-- Hotel mode: building/hotel section divider -->
            <header v-if="group.title != null" class="re-group__head" @click="toggleGroup(group.id)">
              <span class="re-group__icon" :class="group.id === '_unassigned' ? 're-group__icon--none' : ''">
                <feather-icon :icon="group.id === '_unassigned' ? 'AlertTriangleIcon' : 'LayersIcon'" size="15"/>
              </span>
              <span class="re-group__title">{{ group.title }}</span>
              <b-badge pill variant="light-primary" class="re-group__count">
                {{ group.rooms.length }} {{ msg('hm_rooms') }}
              </b-badge>
              <b-badge pill variant="light-success" class="re-group__count" v-if="groupActiveCount(group) > 0">
                {{ groupActiveCount(group) }} {{ msg('Active') }}
              </b-badge>
              <span class="re-group__spacer"></span>
              <b-button v-if="group.id !== '_unassigned'" size="sm" variant="flat-primary" class="re-group__open"
                        @click.stop="openBuilding(group.id)">
                <feather-icon icon="ExternalLinkIcon" size="13"/>
              </b-button>
              <feather-icon class="re-group__chev" :icon="collapsedGroups[group.id] ? 'ChevronRightIcon' : 'ChevronDownIcon'" size="18"/>
            </header>

            <template v-if="group.title == null || !collapsedGroups[group.id]">
          <div v-for="re in group.rooms" :key="re.id"
               class="re-pcard"
               :class="{'re-pcard--selected': selectedRealEstatesIds.includes(re.id)}"
               @click="onRowClick(re)">
            <span class="re-pcard__accent" :class="isRealEstateActive(re) ? 'is-on' : 'is-off'"/>

            <div class="re-pcard__lead" @click.stop>
              <b-form-checkbox class="re-pcard__check" v-model="selectedRealEstatesIds" :value="re.id"/>
              <div class="re-pcard__thumb" @click="onRowClick(re)">
                <img v-if="re.attachments && re.attachments[0]"
                     :src="mergeSrcUrl(getRealEstatesWebSite, re.attachments[0])" alt=""/>
                <feather-icon v-else icon="HomeIcon" size="24"/>
              </div>
            </div>

            <div class="re-pcard__main">
              <div class="re-pcard__top">
                <span class="re-pcard__name">{{ re.name || msg('Unnamed property') }}</span>
                <b-badge class="re-pcard__idchip" v-clipboard:copy="re.id" v-clipboard:success="onCopy"
                         v-clipboard:error="onError" @click.native.stop>
                  <feather-icon icon="CopyIcon" size="10"/> {{ re.id }}
                </b-badge>
              </div>
              <div class="re-pcard__addr">
                <feather-icon icon="MapPinIcon" size="13"/>
                <span>{{ getFullAddress(re) }}<template v-if="re.zip"> &middot; {{ re.zip }}</template></span>
              </div>
              <div class="re-pcard__meta">
                <span v-if="channelInfo(re)" class="re-pcard__chip re-pcard__chip--channel"
                      :style="{ color: channelInfo(re).color, background: channelInfo(re).bg }"
                      v-b-tooltip.hover :title="msg('Channel Manager')">
                  <feather-icon icon="Share2Icon" size="12"/>{{ channelInfo(re).name }}
                </span>
                <span v-if="re.realEstateArea && re.realEstateArea.rooms != null" class="re-pcard__chip"
                      v-b-tooltip.hover :title="msg('Rooms')">
                  <feather-icon icon="GridIcon" size="12"/>{{ re.realEstateArea.rooms }} {{ msg('Rooms') }}
                </span>
                <span v-if="re.realEstateArea && re.realEstateArea.livingSpace != null" class="re-pcard__chip"
                      v-b-tooltip.hover :title="msg('Living space')">
                  <feather-icon icon="Maximize2Icon" size="12"/>{{ re.realEstateArea.livingSpace }}&nbsp;m&#178;
                </span>
                <span v-if="re.realEstateArea && re.realEstateArea.totalSpace != null" class="re-pcard__chip"
                      v-b-tooltip.hover :title="msg('Total space')">
                  <feather-icon icon="SquareIcon" size="12"/>{{ re.realEstateArea.totalSpace }}&nbsp;m&#178;
                </span>
                <span v-if="re.apt" class="re-pcard__chip" v-b-tooltip.hover :title="msg('Apt')">
                  <feather-icon icon="HashIcon" size="12"/>{{ re.apt }}
                </span>
                <span v-if="isHotelMode && getRoomCategoriesMap[re.roomCategoryId] != null"
                      class="re-pcard__chip re-pcard__chip--roomcat"
                      :style="{color: '#fff', background: getRoomCategoriesMap[re.roomCategoryId].color || '#7367f0'}"
                      v-b-tooltip.hover :title="msg('roomcategory')">
                  <feather-icon icon="TagIcon" size="12"/>{{ getRoomCategoriesMap[re.roomCategoryId].name }}
                </span>
                <span v-else-if="re.imageCategory" class="re-pcard__chip re-pcard__chip--cat"
                      v-b-tooltip.hover :title="msg('Room category')">
                  <feather-icon icon="TagIcon" size="12"/>{{ re.imageCategory }}
                </span>
                <span v-if="buildingsMap[re.buildingId]" class="re-pcard__chip"
                      v-b-tooltip.hover :title="msg('Building')">
                  <feather-icon icon="LayersIcon" size="12"/>{{ buildingsMap[re.buildingId].address }}
                </span>
                <span v-if="re.contactPerson" class="re-pcard__chip" v-b-tooltip.hover :title="msg('Owner')">
                  <feather-icon icon="UserIcon" size="12"/>{{ re.contactPerson.firstname }} {{ re.contactPerson.lastname }}
                </span>
                <span v-if="re.totalReservations" class="re-pcard__chip re-pcard__chip--res"
                      v-b-tooltip.hover :title="msg('totalReservations')">
                  <feather-icon icon="CalendarIcon" size="12"/>{{ re.totalReservations }}
                </span>
                <span class="re-pcard__chip" v-b-tooltip.hover :title="msg('Todos')">
                  <feather-icon icon="CheckSquareIcon" size="12"/>{{ countDoneItems(re.todoList) }}
                </span>
                <span v-for="userId in (re.userIds || [])" :key="userId" class="re-pcard__chip re-pcard__chip--agent"
                      v-b-tooltip.hover :title="msg('Agent/s')">
                  <feather-icon icon="UserCheckIcon" size="12"/>{{ getUserFullName(userId) }}
                </span>
              </div>
              <div v-if="re.realEstateTreasure" class="re-pcard__platforms" @click.stop>
                <b-button v-if="re.realEstateTreasure.bookingLink && re.realEstateTreasure.bookingLink.trim()!==''"
                          class="re-plat" style="background-color: rgba(49,121,231,0.85)!important;"
                          v-clipboard:copy="re.realEstateTreasure.bookingLink" v-clipboard:success="onLinkCopy"
                          v-clipboard:error="onError" v-b-tooltip.hover title="Booking">B</b-button>
                <b-button v-if="re.realEstateTreasure.airbnbLink && re.realEstateTreasure.airbnbLink.trim()!==''"
                          class="re-plat" style="background-color: rgba(255,82,88,0.9)!important;"
                          v-clipboard:copy="re.realEstateTreasure.airbnbLink" v-clipboard:success="onLinkCopy"
                          v-clipboard:error="onError" v-b-tooltip.hover title="Airbnb">A</b-button>
                <b-button v-if="re.realEstateTreasure.expediaLink"
                          class="re-plat" style="background-color: #f5a623!important;"
                          v-clipboard:copy="re.realEstateTreasure.expediaLink" v-clipboard:success="onLinkCopy"
                          v-clipboard:error="onError" v-b-tooltip.hover title="Expedia">E</b-button>
                <b-button v-if="re.realEstateTreasure.treasureLink"
                          class="re-plat" style="background-color: rgba(199,166,128,0.95)!important;"
                          v-clipboard:copy="re.realEstateTreasure.treasureLink" v-clipboard:success="onLinkCopy"
                          v-clipboard:error="onError" v-b-tooltip.hover title="Treasure Home">T</b-button>
                <b-button v-if="re.realEstateTreasure.agodaLink"
                          class="re-plat" style="background-color: #2ecc71!important;"
                          v-clipboard:copy="re.realEstateTreasure.agodaLink" v-clipboard:success="onLinkCopy"
                          v-clipboard:error="onError" v-b-tooltip.hover title="Agoda">A</b-button>
                <b-button v-if="re.realEstateTreasure.vrboLink && re.realEstateTreasure.vrboLink.trim()!==''"
                          class="re-plat" style="background-color: #3b7dd8!important;"
                          v-clipboard:copy="re.realEstateTreasure.vrboLink" v-clipboard:success="onLinkCopy"
                          v-clipboard:error="onError" v-b-tooltip.hover title="Vrbo">V</b-button>
              </div>
            </div>

            <div class="re-pcard__side" @click.stop>
              <div class="re-pcard__badges">
                <b-badge :variant="isRealEstateActive(re) ? 'light-success' : 'light-danger'">
                  {{ isRealEstateActive(re) ? msg('Active') : msg('Inactive') }}
                </b-badge>
                <b-badge v-if="re.status" :variant="'light-' + statusVariants(re.status)">{{ re.status }}</b-badge>
                <b-badge :variant="re.nukiStatus != null ? 'light-primary' : 'light-secondary'">
                  Nuki: {{ re.nukiStatus != null ? msg(re.nukiStatus) : '—' }}
                </b-badge>
                <b-badge :variant="re.advertise === true ? 'light-success' : 'light-secondary'">
                  {{ getFeaturedStatus(re) }}
                </b-badge>
              </div>
              <div class="re-pcard__pricerow">
                <span class="re-pcard__price">{{ priceLabel(re) }}</span>
                <span class="re-pcard__updated">
                  <feather-icon icon="ClockIcon" size="12"/>{{ $moment(re.updatedDate, 'YYYY-MM-DD HH:mm').format("DD/MM/YY HH:mm") }}
                </span>
              </div>
              <div class="re-pcard__actions">
                <b-button class="re-act re-act--reserve" v-b-tooltip.hover :title="msg('Reservation count')"
                          @click="showReservationCount(re)">
                  <feather-icon icon="CalendarIcon" size="15"/>
                </b-button>
                <b-button class="re-act re-act--disable" v-b-tooltip.hover :title="msg('Disable')"
                          @click="info(re, 0, $event.target)">
                  <feather-icon icon="SlashIcon" size="15"/>
                </b-button>
                <b-button class="re-act re-act--open" v-b-tooltip.hover :title="msg('Edit')"
                          @click="onRowClick(re)">
                  <feather-icon icon="ExternalLinkIcon" size="15"/>
                </b-button>
              </div>
            </div>
          </div>
            </template>
          </div>

          <div v-if="realEstates.length === 0" class="re-empty">
            <div class="re-empty__art">
              <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="26" y="54" width="68" height="44" rx="7" fill="#7367F0" fill-opacity="0.12"/>
                <path d="M60 22 L98 54 H22 Z" fill="#7367F0" fill-opacity="0.9"/>
                <rect x="38" y="66" width="16" height="16" rx="3" fill="#ffffff"/>
                <rect x="66" y="66" width="16" height="32" rx="3" fill="#28C76F"/>
                <circle cx="93" cy="31" r="9" fill="#FF9F43" fill-opacity="0.85"/>
                <circle cx="23" cy="92" r="6" fill="#00CFE8" fill-opacity="0.6"/>
              </svg>
            </div>
            <div class="re-empty__title">{{ msg('No properties match your filters') }}</div>
            <div class="re-empty__sub">{{ msg('Try adjusting or clearing your filters') }}</div>
            <b-button v-if="activeFilterCount > 0" class="re-empty__btn" @click="resetFilters">
              <feather-icon icon="RefreshCcwIcon" size="14"/>{{ msg('Clear all') }}
            </b-button>
          </div>
        </div>

        <b-modal id="reservation-modal" title="Reservation Count" v-model="showReservationModal" hide-footer>
          <div class="text-center my-3">
            <b-spinner v-if="loadingReservation" class="mb-2" label="Loading..."></b-spinner>
            <template v-else>
              <p style="font-weight: bold">{{ propertyName }}</p>
              <p >{{ reservationCountText }}</p>

            </template>

          </div>
        </b-modal>


        <b-modal ref="myModal" @ok="confirmAndCopy" size="sm"
                 :ok-disabled="duplicate.name==null || duplicate.apt==null ||duplicate.rooms==null ||duplicate.area==null ||duplicate.peoples==null">
          <template #modal-title>
            {{ msg('duplicateestate') }}
          </template>

          <b-col cols="12" md="12" lg="12">
            <b-form-group :label="msg('name')" label-for="realEstate-name">
              <b-form-input v-model="duplicate.name" name="check-button">
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="12" lg="12">
            <b-form-group :label="msg('apt')" label-for="realEstate-name">
              <b-form-input v-model="duplicate.apt">
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="12" lg="12">
            <b-form-group :label="msg('People capacity')" label-for="realEstate-name">
              <b-form-input v-model="duplicate.peoples" name="check-button" type="number">
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="12" lg="12">
            <b-form-group :label="msg('rooms')" label-for="realEstate-name">
              <b-form-input v-model="duplicate.rooms" name="check-button" type="number">
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="12" lg="12">
            <b-form-group :label="msg('area')" label-for="realEstate-name">
              <b-form-input v-model="duplicate.area" name="check-button" type="number">
              </b-form-input>
            </b-form-group>
          </b-col>
        </b-modal>
        <b-modal :id="infoModal.id" :title="msg('Confirmation')" @ok="archiveRealEstateById(infoModal.propertyId)"
                 ok-variant="danger" @hide="resetInfoModal">
          <p class="my-4">Are you sure you want to ARCHIVE this real estate? {{ '(' + addressName + ')' }}</p>
        </b-modal>

        <b-modal size="lg" :id="featureInfoModal.id"
                 :title="msg('Confirmation')"
                 :ok-title="featureInfoModal.type==='NONE'? msg('Deactivate'):
                 (featureInfoModal.type==='NORMAL'? msg('Activate for free') : msg('Chargeable activation'))"
                 @ok="featureRealEstateById(featureInfoModal.propertyId,featureInfoModal.type)"
                 ok-variant="primary" @hide="resetFeatureInfoModal">

          <div class="w-100" style="text-align: center">
            <p class="mb-3" style="color:white;font-size: x-large;font-weight: bold;">Inserat Optionen</p>
            <b-form-group v-slot="{ ariaDescribedby }">
              <b-row class="features-group">
                <b-col cols="6" class="mb-1">
                  <b-form-radio name="features-group" :aria-describedby="ariaDescribedby"
                                class="w-100"
                                value="NONE" v-model="featureInfoModal.type"
                                button-variant="outline-danger" button>
                    <div class="text-danger">
                      <p class="mb-0"><i class="far fa-stop-circle"></i></p>
                      <p>Nicht Inserieren</p>
                    </div>
                  </b-form-radio>
                </b-col>

                <b-col cols="6" class="mb-1">
                  <b-form-radio name="features-group"
                                :aria-describedby="ariaDescribedby"
                                value="NORMAL"
                                v-model="featureInfoModal.type"
                                button-variant="outline-primary" button>
                    <div>
                      <p class="mb-0"><i class="far fa-play-circle"></i></p>
                      <p>Normales Inserieren (Kostenlos)</p>
                    </div>
                  </b-form-radio>
                </b-col>


                <b-col cols="6" class="mb-1">
                  <b-form-radio name="features-group"
                                :aria-describedby="ariaDescribedby"
                                v-model="featureInfoModal.type"
                                value="PROPERTYPAGE"
                                button-variant="outline-success" button>
                    <div class="text-success">
                      <p class="mb-0"><i class="fas fa-tags"></i></p>
                      <p class="mb-0">Ausgewählte Immobilien- Immobilienseite</p>
                      <p class="mb-0 font-weight-bold"><strong>€11/30 Tage</strong></p>
                    </div>
                  </b-form-radio>
                </b-col>

                <b-col cols="6" class="mb-1">
                  <b-form-radio name="features-group"
                                :aria-describedby="ariaDescribedby"
                                v-model="featureInfoModal.type"
                                value="SEARCHPAGE" button-variant="outline-success" button>
                    <div class="text-success">

                      <p class="mb-0"><i class="fas fa-coins"></i></p>
                      <p class="mb-0"> Ausgewählte Immobilien- Suchseite</p>
                      <p class="mb-0 font-weight-bold"><strong>€17/30 Tage</strong></p>

                    </div>
                  </b-form-radio>
                </b-col>

                <b-col cols="12" class="mb-1">
                  <b-form-radio name="features-group" value="HOMEPAGE" :aria-describedby="ariaDescribedby"
                                v-model="featureInfoModal.type"
                                button-variant="outline-success" button>
                    <div class="text-success">

                      <p class="mb-0"><i class="far fa-gem"></i></p>
                      <p class="mb-0"> Ausgewählte Immobilien- Startseite</p>
                      <p class="mb-0 font-weight-bold"><strong>€60/30 Tage</strong></p>

                    </div>
                  </b-form-radio>
                </b-col>
              </b-row>


            </b-form-group>


          </div>
        </b-modal>


        <!--Table Paginator-->
        <div class="text-center w-100">
          <div class="d-inline-block">
            <b-pagination
                v-model="page.currentPage"
                :total-rows="page.total"
                :per-page="page.amount"
                first-number
                last-number
                @input="refresh"
            />
          </div>
          <div class="d-inline-block px-2"><strong>{{ msg('Total') }}</strong> {{ page.total }}</div>
        </div>
      </b-card-body>
    </b-card>



    <b-modal no-fade v-model="dialogs.realEstate" size="xl" ref="realEstate-modal" id="realEstate-modal"
             :hide-footer="true" no-close-on-backdrop centered>
      <realEstate-panel ref="realEstatePanel" @submitted="onSubmitted" :all-buildings="buildings"/>
      <div class="text-center" style="justify-content:center">
        <b-button variant="outline-primary" class="btn-dialog" @click="submitRealEstate">
          {{ msg('Save') }}
        </b-button>
      </div>
    </b-modal>

    <!--Excel building-->
    <b-modal v-model="dialogs.importrealestates" size="xl" @show="dismissAllPopovers" centered hide-footer
             no-close-on-backdrop>
      <template #modal-title>{{
          msg("importrealestatesoptional-(selectbuildingfromthelistor/andhostawayexport)")
        }}
      </template>
      <div>
        <b-row class="mb-1 ">

          <b-col cols="4" class="d-flex align-items-center justify-content-start">
            <b-button class="text-nowrap" variant="success" v-b-tooltip
                      :title="msg('Click here to download the excel template')"
                      @click="downloadExcelTemplateRealEstates">
              <i class="far fa-file-excel mr-1"></i>
              <span>Excel template</span>
            </b-button>
          </b-col>


          <b-col cols="4" class="d-flex align-items-center justify-content-start mr-1 ">
            <v-select
                append-to-body
                :dir="isRTL ? 'rtl' : 'ltr'"
                v-model="excelBuid"
                placeholder="Branch"
                :options="allBranches"
                class="w-100"
                label="name"
                :reduce="(branch) => branch.id"
                :clearable="false"

            />
          </b-col>
          <b-col v-if="buildings!=null && buildings.length>0" cols="4" md="3"
                 class="d-flex align-items-center justify-content-start mr-1 ">
            <v-select
                append-to-body
                placeholder="Building (Optional)"
                :dir="isRTL ? 'rtl' : 'ltr'"
                v-model="buildingId"
                :options="buildings"
                class="w-100"
                label="address"
                :reduce="(building) => building.id"
            />
          </b-col>
        </b-row>
        <b-row v-if="excelBuid!=null">
          <b-col cols="4" class="mt-2 align-items-center  justify-content-end ">
            <div>
              <b-form-file
                  v-model="file"
                  :placeholder="msg('Drop your client excel file here...')"
                  :drop-placeholder="msg('Drop file here...')"
              />
            </div>

          </b-col>
          <b-col cols="1" class="d-flex align-items-center mt-1">
            <b-button :disabled="file==null" variant="danger" @click="resetFile">
              <span class="text-nowrap">{{ msg('Reset file') }}</span>
            </b-button>
          </b-col>
        </b-row>
      </div>

    </b-modal>

    <!--Connect to channel-->
    <channel-manager-modal ref="channelManagerModal"/>

    <b-modal v-model="showModal.show" :title="msg('Confirmation')" @ok="copyToNextExpenses"
             ok-variant="danger" :cancel-title="msg('cancel')" @hide="showModal.show=false">
      <p class="my-4">{{ msg('confirmcopyexpenses') }}</p>
    </b-modal>


    <b-modal ref="modalRef" size="lg" :title="msg('Listings from external platform that arent currently in your system')"
             :hide-footer="true">
      <div v-if="fetchedListingMap==null || fetchedListingMap.size === 0">Loading...</div>
      <template v-else>
        <p>{{$t('Pick an owner')}}</p>
        <client-picker class="mb-2" v-model="ownerId" is-owner/>

        <p>{{$t('Total')}}: {{fetchedRealEstatesCount()}}</p>

        <div class="mb-2" v-for="(value, key) in fetchedListingMap" :key="key">
          <b-button :disabled="ownerId==null" variant="success" size="sm"
                    @click="importListingFromExternalPlatform(key)">Import
          </b-button>
          <span class="mx-1">{{ value }}</span>
        </div>
      </template>
    </b-modal>
    <!--Fetch from hostaway popover-->
    <b-popover size="xl" target="import-from-hostaway-button" triggers="click" placement="bottom"
               @show="dismissAllPopovers">
      <template #title>{{ msg("Import from External channel manager") }}</template>
      <div style="max-width: 800px; min-width: 500px">
        <div class="mb-1">
          <v-select
              append-to-body
              :dir="isRTL ? 'rtl' : 'ltr'"
              v-model="hostawayBuid"
              placeholder="Branch"
              :options="allBranches"
              class="w-100"
              label="name"
              :reduce="(branch) => branch.id"
              :clearable="false"

          />
        </div>
          <div style="max-width: 800px; min-width: 500px">
          <v-select
              append-to-body
              v-if="buildings!=null && buildings.length>0"
              placeholder="Building"
              :dir="isRTL ? 'rtl' : 'ltr'"
              v-model="importBuildingId"
              :options="buildings"
              class="w-100 mb-2"
              label="address"
              :reduce="(building) => building.id"
          />
        </div>


        <b-button block :disabled="importBuildingId==null|| hostawayBuid == null || fetchListLoading" class="text-nowrap" variant="warning"
                  v-b-tooltip @click="fetchListFromExternal">
          <i v-if="fetchListLoading" class="fas fa-spinner fa-spin mr-1"></i> <!-- Spinner -->
          <i v-else class="fas fa-list mr-1"></i> <!-- Icon -->
          <span v-if="fetchListLoading">Loading...</span> <!-- Loading text -->
          <span v-else>Fetch list</span> <!-- Default text -->
        </b-button>

      </div>
    </b-popover>
    <template v-if="realEstates!=null">
      <b-modal hide-footer no-close-on-backdrop
               v-model="dialogs.importimages"
               title="Import Images"
               size="lg"
               centered
               body-class="p-4"
               footer-class="d-flex justify-content-end"
               class="shadow-lg rounded"
      >
        <!-- Modal Body -->
        <div class="mb-4">
          <h5 class="text-muted mb-3">Select Real Estates</h5>
          <b-list-group>
            <b-list-group-item
                v-for="realEstate in realEstates"
                :key="realEstate.id"
                class="d-flex align-items-center justify-content-between"
            >
              <span>{{ realEstate.fullAddress }} ({{ realEstate.name }})</span>
              <b-form-checkbox
                  v-model="selectedRealEstateIds"
                  :value="realEstate.id"
              ></b-form-checkbox>
            </b-list-group-item>
          </b-list-group>
        </div>

        <!-- Dropzone (visible only if at least one real estate is selected) -->
        <div
            v-if="selectedRealEstateIds.length > 0"
            class="mt-4 p-3 border rounded shadow-sm"

        >
          <vue-dropzone
              id="dropzone"
              ref="dropzone"
              :options="dropzoneOptions"
              :useCustomSlot="true"
              :duplicateCheck="true"
              @vdropzone-complete="uploadComplete"
              @vdropzone-success="uploadSuccess"
              class="dropzone-modern"
          >
            <div class="dropzone-custom-content">
              <h3 class="dropzone-custom-title text-center">
                Drag and drop to upload content!
              </h3>
              <div class="subtitle text-center">
                ...or click to select a file from your computer
              </div>
            </div>
          </vue-dropzone>
        </div>

      </b-modal>
    </template>


  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import RealEstatePanel from './realEstatePanel'
import PlatformLogo from "@/components/widgets/PlatformLogo.vue";
import mixinFilter from "@/mixins/mixinFilter";
import ClientPicker from "@/components/components/picker/clientPicker.vue";
import BreadcrumbButtons from "@/components/widgets/breadcrumbButtons.vue";
import ChannelManagerModal from "@/components/modals/channelManagerModal.vue";
import vue2Dropzone from 'vue2-dropzone'
import PageHero from '@/components/PageHero.vue'

const realEstateFilter = () => ({
  like: {search: '', name: "", shortDescription: ""},
  eq: {type: "", realEstateArea: {}, featureStatus: null},
  lte: {realEstatePrices: {}},
  gte: {realEstatePrices: {}},
  in: {}
})

const buildingFilter = () => ({
  like: {},
  eq: {},
  lte: {},
  gte: {},
  in: {}
})
export default {
  name: 'RealEstate',
  mixins: [mixinFilter],
  components: {
    PageHero,
    ChannelManagerModal,
    BreadcrumbButtons,
    ClientPicker,
    // eslint-disable-next-line vue/no-unused-components
    PlatformLogo,
    vueDropzone: vue2Dropzone,

    RealEstatePanel
  },
  data() {
    return {
      loading: true,
      buildingFilter: buildingFilter(),
      amount: 10,
      reservationCountText: '',
      propertyName: '',
      loadingReservation: false,

      showCounterModal: false,
      ownerId: null,
      importBuildingId: null,
      buid: null,
      platformLogos: {
        'Vrbo': 'https://treasurehome.at/uploads/images/vrbo.png',
        'Booking': 'https://treasurehome.at/uploads/images/booking.png',
        'Airbnb': 'https://treasurehome.at/uploads/images/airbnb.png',
        'Treasure Home': 'https://treasurehome.at/uploads/images/treasurehome.png',
        'Agoda': 'https://treasurehome.at/uploads/images/agoda.png',
        'Expedia': 'https://treasurehome.at/uploads/images/expedia.png',
      },
      isCollapsed: false,
      isLoading: false,
      showModal: {
        id: 'show-modal',
        title: '',
        content: '',
        show: false,
        expenseId: null,
      }, showReservationModal: {
        id: 'show-modal',
        title: '',
        content: '',
        show: false,
        expenseId: null,
      },
      realEstates: [],
      buildings: [],
      buildingsMap: [],
      buildingId: null,
      hostawayBuid: null,
      fetchedListingMap: null,
      fetchListLoading: false,
      dropzoneOptions: {},

      excelBuid: null,
      excelUrl: "",
      duplicate: {
        name: null,
        apt: null,
        peoples: null,
        area: null,
        rooms: null,

      },
      selectedRealEstateIds: [],

      file: null,
      selectedRealEstates: [],
      fields: [],
      filter: realEstateFilter(),
      sort: {
        by: 'id',
        desc: true,
        direction: 'desc'
      },

      page: {
        amount: 10,
        currentPage: 1,
        total: 0
      },
      collapsedGroups: {},
      modalContent: '',
      dialogs: {realEstate: false, importrealestates: false, importimages: false},
      addressName: null,
      infoModal: {
        id: 'info-modal',
        title: '',
        content: ''
      },
      featureInfoModal: {
        id: 'feature-modal',
        type: null,
        title: '',
        content: ''
      },
      channelInfoModal: {
        id: 'channel-modal',
        channelMap: null,
        type: null,
        title: '',
        content: ''
      },
      //Listeners
      listeners: {
        refresh: () => {
        }
      }
    }
  },
  watch: {
    selectedRealEstateIds: {
      handler() {
        this.dropzoneOptions.headers =
            {
              'Authorization': `Bearer ${localStorage.Bearer}`,
              'realEstateIds': this.selectedRealEstateIds.join(',')
            }
      }
    },
    file: {
      handler() {
        this.uploadFile()
      }
    },
    filter: {
      deep: true,
      handler() {
        this.refresh()
      }
    },
    fields: {
      deep: true,
      handler() {
        localStorage["real-estates-fields"] = JSON.stringify(this.fields);
      }
    },
    selectedRealEstates: {
      handler() {
        this.$emit("selectedRealEstates", [...this.selectedRealEstates])
      }
    }
  },
  computed: {
    ...mapGetters('data', ['getOrganizationWebSite', 'getRealEstatesWebSite']),
    ...mapGetters('data', ['getUserFullName', 'getUserAcronyms']),
    ...mapGetters('data', ['getAllUsers', 'getUsersInTeam', 'getUserRole', 'getRoles', 'getRoleName']),
    ...mapGetters('realEstate', ['allStatus', 'statusVariants', 'getPrices', 'getFeaturedStatus', "getFullAddress"]),
    ...mapGetters('translator', ['msg']),
    ...mapGetters('data', ['getAllBranches']),
    ...mapGetters('data', ['isHotelMode']),
    ...mapGetters('roomCategory', ['getRoomCategoriesMap']),
    /**
     * Hotel mode: rooms grouped under their building (section dividers).
     * Off: a single title-less pseudo-group renders the identical flat list.
     */
    displayGroups() {
      if (!this.isHotelMode)
        return [{id: '_all', title: null, rooms: this.realEstates}];
      const groups = {};
      (this.realEstates || []).forEach(re => {
        const building = this.buildingsMap[re.buildingId];
        const gid = building != null ? re.buildingId : '_unassigned';
        if (groups[gid] == null) {
          groups[gid] = {
            id: gid,
            title: building != null ? (building.company || building.address || gid) : this.msg('unassigned'),
            rooms: []
          };
        }
        groups[gid].rooms.push(re);
      });
      return Object.values(groups).sort((a, b) => {
        if (a.id === '_unassigned') return 1;
        if (b.id === '_unassigned') return -1;
        return String(a.title).localeCompare(String(b.title));
      });
    },
    filterText() {
      if (!this.isCollapsed) {
        return this.msg('Show filter')
      } else {
        return this.msg('Hide filter')
      }
    },
    allBranches() {
      return Object.values(this.getAllBranches)
    },
    branchMap() {
      const m = {};
      (this.allBranches || []).forEach(b => { if (b && b.id != null) m[b.id] = b; });
      return m;
    },

    selectedFields() {
      let fields = this.fields.filter(f => f.active);
      return (fields.length > 0) ? fields : this.fields;
    },
    selectedRealEstatesIds: {
      get() {
        return this.selectedRealEstates.map(p => p.id);
      },
      set(selectedRealEstatesIds) {
        let realEstates = this.realEstates.toObject("id");
        this.selectedRealEstates = selectedRealEstatesIds.map(pid => realEstates[pid]);
      }
    },
    isAllRealEstatesSelected() {
      let ids = this.selectedRealEstates.map(p => p.id);
      return this.realEstates.filter(p => !ids.includes(p.id)).length === 0;
    },
    allRealEstateStatus() {
      return this.allStatus.map(status => ({
        value: status.value,
        text: this.msg(status.text)
      }))
    },
    allAgents() {

      return this.getAllUsers
          .filter(us => us.roleId === 18)
          .map(us => ({
            id: us.id,
            name: (us.firstname + ' ' + us.lastname)
          }))
    },
    allRealEstates() {
      return this.realEstates
    },
    sortLabel() {
      return this.sort.direction === 'asc' ? this.msg('Oldest first') : this.msg('Newest first');
    },
    activeFilterCount() {
      return this.countFilters(false);
    },
    advancedFilterCount() {
      return this.countFilters(true);
    }
  },
  created() {
    let $this = this;
    this.setFields();
    this.filter = this.loadCachedFilters(realEstateFilter);
    if (this.isHotelMode) {
      // bigger default page so the grouped-by-hotel view stays useful
      this.page.amount = 100;
      this.$store.dispatch('roomCategory/getAllForOrg').catch(() => {});
    }
    this.refresh()
    this.$root.$on('crm::branch-updated', (this.listeners.refresh = () => {
      $this.refresh()
    }))
    this.$root.$on('crm::lang-updated', this.setFields)
  },
  mounted() {
    this.$nextTick(() => {
      window.scrollTo(0, 0);
      const el = document.querySelector('.content-body') || document.documentElement;
      if (el && el.scrollTop !== undefined) el.scrollTop = 0;
    });
  },
  beforeDestroy() {
    this.$root.$off('crm::branch-updated', this.listeners.refresh)
    this.$root.$off('crm::lang-updated', this.setFields)
  },
  methods: {
    isRealEstateActive(item) {
      return (!item.deleted || item.deleted === false) && item.advertise === true;
    },
    toggleGroup(groupId) {
      this.$set(this.collapsedGroups, groupId, this.collapsedGroups[groupId] !== true);
    },
    groupActiveCount(group) {
      return group.rooms.filter(re => this.isRealEstateActive(re)).length;
    },
    openBuilding(buildingId) {
      this.$router.push('/building/edit/' + buildingId);
    },
    priceLabel(re) {
      const p = re && re.realEstatePrices;
      if (p) {
        if (re.marketingType && re.marketingType.rent && p.rentTotal != null) return '€' + p.rentTotal + '/mo';
        if (p.basePricePerNight != null) return '€' + p.basePricePerNight + '/night';
        if (p.rentTotal != null) return '€' + p.rentTotal;
      }
      return '—';
    },
    channelInfo(re) {
      if (!re || re.buid == null) return null;
      const branch = this.branchMap[re.buid];
      const type = branch && branch.externalApiType;
      if (!type || type === 'UNKNOWN') return null;
      const map = {
        HOSTAWAY: { name: 'Hostaway', color: '#ff7a45', bg: 'rgba(255,122,69,0.14)' },
        LODGIFY: { name: 'Lodgify', color: '#00b8d4', bg: 'rgba(0,184,212,0.14)' },
        APALEO: { name: 'Apaleo', color: '#7367f0', bg: 'rgba(115,103,240,0.14)' },
        CLOUDBEDS: { name: 'Cloudbeds', color: '#28c76f', bg: 'rgba(40,199,111,0.14)' },
        SITEMINDER: { name: 'SiteMinder', color: '#ea5455', bg: 'rgba(234,84,85,0.14)' },
        RENTAL: { name: 'Rental United', color: '#ff9f43', bg: 'rgba(255,159,67,0.14)' },
      };
      return map[type] || { name: type, color: '#82868b', bg: 'rgba(130,134,139,0.14)' };
    },
    setSort(dir) {
      this.sort.direction = dir;
      this.sort.desc = dir === 'desc';
      this.refresh();
    },
    // Active / Inactive segmented control -> filter.eq.advertise (null = all, true = active, false = inactive)
    setAdvertise(val) {
      this.$set(this.filter.eq, 'advertise', val);
    },
    // Count applied filters (read-only helper for the filter badges); advancedOnly limits to the "More filters" section
    countFilters(advancedOnly) {
      const f = this.filter || {};
      const has = (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
      const like = f.like || {};
      const eqF = f.eq || {};
      const inF = f.in || {};
      const area = eqF.realEstateArea || {};
      const gte = (f.gte || {}).realEstatePrices || {};
      const lte = (f.lte || {}).realEstatePrices || {};
      let n = 0;
      ['id', 'address', 'house', 'zip', 'city'].forEach((k) => { if (has(like[k])) n++; });
      ['rooms', 'livingSpace', 'totalSpace'].forEach((k) => { if (has(area[k])) n++; });
      if (has(eqF.hostawayId)) n++;
      if (has(eqF.imageCategory)) n++;
      ['rentTotal', 'rentNetto'].forEach((k) => { if (has(gte[k])) n++; });
      ['rentTotal', 'rentNetto'].forEach((k) => { if (has(lte[k])) n++; });
      if (advancedOnly) return n;
      if (has(like.name)) n++;
      if (has(inF.userIds)) n++;
      if (has(inF.nukiStatus)) n++;
      if (has(eqF.marketingType)) n++;
      if (has(eqF.usageType)) n++;
      if (has(eqF.featureStatus)) n++;
      if (eqF.advertise === true || eqF.advertise === false) n++;
      return n;
    },
    ...mapActions('realEstate', ['copyToNextExpensesRealEstate', 'getExcelTemplateUrl', 'getRealEstateReservationCountById']),
    ...mapActions('realEstate', ['getAllRealEstates', 'getAllRealEstatesWithCounter', 'archiveRealEstate', 'copyLinkSuccess', 'channelExport', 'featureRealEstate', 'deFeatureRealEstate', 'copySuccess']),
    ...mapActions('realEstate', ['getAllDisabledRealEstates', 'activateRealEstate', 'bulkDisable', 'copyRealEstate',
      'fetchRealEstateFromExternalPlatform', 'fetchAllRealEstateFromExternal', 'bulkAdvertise', 'justimmoUpdate']),
    ...mapActions('building', ['getAllBuildings']),
    ...mapActions('realEstate', {_uploadExcel: 'uploadExcel'}),

    showReservationCount(item, index, target) {
      let $this = this;
      this.loadingReservation = true;
      this.showReservationModal = true;
      this.getRealEstateReservationCountById(item.id)
          .then(response => {
            $this.reservationCountText = response.totalReservations;
            $this.propertyName = item.name;
            $this.loadingReservation = false;

          });

    },
    openImageModal() {
      this.dialogs.importimages = true;
      this.selectedRealEstateIds = [];
    },
    uploadComplete() {
      // Show the toast
      this.$bvToast.toast("Files uploaded successfully!", {
        title: "Upload Complete",
        variant: "success",
        solid: true,
        autoHideDelay: 2000, // Auto-hide after 2 seconds
        toaster: "b-toaster-top-right", // Position the toast at the top-right
      });

      // Close the dialog and refresh after 2 seconds
      setTimeout(() => {
        this.dialogs.importimages = false;
        this.refresh();
      }, 2000);
    },
    uploadSuccess(file, response) {
      console.log("File uploaded successfully:", file, response);
    },

    loadDropZone() {
      this.dropzoneOptions = {
        url: window.config.apiUrl + 'realEstate/upload/imgMultiple',
        headers: {
          'Authorization': `Bearer ${localStorage.Bearer}`,
          'realEstateIds': this.selectedRealEstateIds.join(',')
        },
        paramName: 'file', // The name expected by the backend for the file parameter
        createImageThumbnails: false,
        thumbnailWidth: 200,
        thumbnailHeight: 200,
        addRemoveLinks: true,
        maxFilesize: 100, // Adjust to suitable size in MB
        maxFiles: 10, // Adjust to desired file limit
        uploadMultiple: true, // Send multiple files in a single request
        multiple: true, // Allow multiple files to be uploaded
        parallelUploads: 10, // Number of files to process in one go
        acceptedFiles: "image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.tsv,.ppt,.pptx,.mp4,.mp3"
      };
    },
    fetchListFromExternal() {
      this.fetchListLoading = true;
      this.fetchAllRealEstateFromExternal({buid: this.hostawayBuid,buildingId:this.importBuildingId})
          .then((ans) => {
            this.fetchedListingMap = ans;
            this.$refs.modalRef.show();
            this.fetchListLoading = false;
          })
    },
    fetchedRealEstatesCount() {
      // { "b1": {...}, "b2": {...}, ... }
      const all = Object.values(this.fetchedListingMap);
      return all.length;
    },

    importListingFromExternalPlatform(channelId) {
      this.fetchListLoading = true;
      this.fetchRealEstateFromExternalPlatform({
        buid: this.hostawayBuid, channelId: channelId,
        ownerId: this.ownerId, buildingId: this.importBuildingId
      })
          .then((ans) => {
            this.fetchListFromExternal();

          })
    },

    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    uploadFile() {
      let $this = this
      if (this.file == null)
        return;

      let paramMap = {"buildingId": this.buildingId, "buid": this.excelBuid}
      this._uploadExcel({file: this.file, params: paramMap}).finally(() => $this.refresh());

    },
    downloadExcelTemplateRealEstates() {
      console.log("excel template url :", this.excelUrl)
      window.open(this.excelUrl, '_blank', 'location=yes,height=570,width=520,scrollbars=yes,status=yes');
    },
    resetFile() {
      this.file = null;
    },
    platformLogoUrl(platformName) {
      return this.platformLogos[platformName];

    },
    copyToClipboard(link) {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      // You can also display a success message here or handle the copied link as needed.
    },

    bulkDisableRealEsates() {
      let $this = this;
      this.bulkDisable(this.selectedRealEstates)
          .then(() => {
            $this.refresh()
          })

    },
    bulkAdvertiseRealEstates(type) {
      let $this = this;
      // this.isLoading = true; // Set loading state to true

      this.bulkAdvertise({ids: this.selectedRealEstates, featureType: type})
          .then(() => {
            $this.refresh();
          })
          .finally(() => {
            $this.isLoading = false; // Reset loading state when API call completes (success or error)
          });
    },

    toggleAllRealEstates(estate) {
      if (estate)
        this.selectedRealEstates = [...this.realEstates];
      else
        this.selectedRealEstates = [];
    },
    archiveRealEstateById(realId) {
      let $this = this;
      this.archiveRealEstate(realId)
          .then(() => {
            $this.refresh()
          })
    },
    featureRealEstateById(realId, type) {
      let $this = this;
      this.featureRealEstate({id: realId, featureType: type})
          .then(() => {
            $this.refresh()
          })
    },
    channelExportRealEstateById(realId, type) {
      let $this = this;
      this.channelExport({id: realId, featureType: type})
          .then(() => {

            $this.refresh()
            $this.resetChannelInfoModal()
          })
    },

    onCopy: function (e) {
      this.copySuccess(e.text)
    },
    onLinkCopy: function (e) {
      console.log(e.text)
      this.copyLinkSuccess(e.text)
    },
    onError: function (e) {
      alert('Failed to copy the text to the clipboard')
    },

    setFields() {
      let $this = this
      let fields = [
        {
          key: 'select',
          label: $this.msg('Bulk select'),
          sortable: false,
          active: true,
        },
        {
          key: 'id',
          label: $this.msg('Actions'),
          sortable: true,
          isActive: false,
          active: true,
        },
        /*{
          key: 'realEstateTreasure',
          label: $this.msg('Platform'),
          sortable: true,
          active: true,
        },*/
        {
          key: 'name',
          label: $this.msg('Name'),
          sortable: true,
          active: true,
        },
        {
          key: 'address',
          label: $this.msg('Address'),
          sortable: true,
          active: true,
        },
        {
          key: 'updatedDate',
          label: $this.msg('Updated at'),
          sortable: true,
          active: true,
        },


        {
          key: 'attachments',
          label: $this.msg('Main image'),
          sortable: true,
          active: true,
        },
        {
          key: 'channelId',
          label: $this.msg('ExternalId'),
          sortable: true,
          active: true,
        },
        {
          key: 'buildingId',
          label: $this.msg('Building'),
          sortable: false,
          active: true,
        },

        {
          key: 'contactPerson',
          label: $this.msg('Owner'),
          sortable: true,
          active: true,
        },
        {
          key: 'apt',
          label: $this.msg('Apt'),
          sortable: true,
          active: true,
        },

        {
          key: 'status',
          label: $this.msg('status'),
          sortable: true,
          active: true,
        },

        {
          key: 'totalReservations',
          label: $this.msg('totalReservations'),
          sortable: true,
          active: true,
        },
        {
          key: 'imageCategory',
          label: $this.msg('Room category'),
          sortable: true,
          active: true,
        },

        {
          key: 'todoList',
          label: $this.msg('Todos'),
          sortable: true,
          active: true,
        },

        {
          key: 'userIds',
          label: $this.msg('Agent/s'),
          sortable: true,
          active: true,
        },
        {
          key: 'realEstatePrices',
          label: $this.msg('Price'),
          sortable: false,
          active: true,
        },
        {
          key: 'realEstateArea.livingSpace',
          label: $this.msg('Living space'),
          sortable: false,
          active: true,
        },
        {
          key: 'realEstateArea.totalSpace',
          label: $this.msg('Total space'),
          sortable: false,
          active: true,
        },
        {
          key: 'realEstateArea.rooms',
          label: $this.msg('Rooms'),
          sortable: true,
          active: true,
        },

        {
          key: 'realEstateTreasure.keyCode',
          label: $this.msg('KeyCode'),
          sortable: true,
          active: true,
        },

        {
          key: 'realEstateTreasure.wifiUsername',
          label: $this.msg('wifiUsername'),
          sortable: true,
          active: true,
        },

        {
          key: 'realEstateTreasure.wifiPassword',
          label: $this.msg('wifiPassword'),
          sortable: true,
          active: true,
        },

        /* {
           key: 'actions',
           label: $this.msg('Actions'),
           sortable: true
         },*/
      ]
      this.fields = fields;
      try {
        let cachedFields = localStorage["real-estates-fields"];
        if (cachedFields != null) {
          let cachedFieldArr = JSON.parse(cachedFields);
          let cachedFieldsMap = cachedFieldArr.toObject("key");
          fields.forEach(field => field.active = cachedFieldsMap[field.key].active != null ? cachedFieldsMap[field.key].active : true);
        }
      } catch (e) {
        localStorage.setItem("real-estates-fields", null);
      }
    },
    updateFromjustimmo() {
      let $this = this;
      if (this.buid == null) return
      this.justimmoUpdate({buid: this.buid})
          .then(() => {
            $this.refresh()
          })
    },
    info(item, index, button) {
      this.infoModal.title = `Row index: ${index}`
      this.infoModal.propertyId = item.id;
      this.addressName = item.address != null ? item.address : "Die Addresse ist leer";
      this.$root.$emit('bv::show::modal', this.infoModal.id, button)
    },
    infoFeature(item, index, button) {
      this.featureInfoModal.type = null;
      this.featureInfoModal.title = `Row index: ${index}`
      this.featureInfoModal.propertyId = item.id;
      this.addressName = item.address != null ? item.address : "Die Addresse ist leer";
      this.$root.$emit('bv::show::modal', this.featureInfoModal.id, button)
    },
    infoChannel(item, index, button) {
      this.channelInfoModal.type = null;
      this.channelInfoModal.title = `Row index: ${index}`
      this.channelInfoModal.propertyId = item.id;
      this.channelInfoModal.channelMap = item.channelExport;
      this.$root.$emit('bv::show::modal', this.channelInfoModal.id, button)
    },
    copyId(id) {
      console.log("the id copied was: ", id)
    },
    openModal(id) {
      this.selectedRealEstateId = id;
      console.log("selectedRealEstateId: ", this.selectedRealEstateId)
      this.$refs.myModal.show();
    },
    dismissAllPopovers() {
      this.$root.$emit('bv::hide::popover')
    },
    // Method to confirm and call the copyRealEstate function
    confirmAndCopy() {
      let $this = this
      this.copyRealEstate({id: this.selectedRealEstateId, duplicate: this.duplicate})
          .then((id) => {
            $this.selectedRealEstateId = null;
            $this.refresh()
            window.open(`/real_estate/deleted_real_estate`, '_blank');

          })
      // After confirmation, close the modal
      this.$refs.myModal.hide();
    },
    resetInfoModal() {
      this.$root.$emit('bv::hide::modal', this.infoModal.id)
    },
    resetFeatureInfoModal() {
      this.featureInfoModal.type = null;
      this.$root.$emit('bv::hide::modal', this.featureInfoModal.id)
    },
    resetChannelInfoModal() {
      this.channelInfoModal.type = null;
      this.$root.$emit('bv::hide::modal', this.channelInfoModal.id)
    },

    refresh() {
      let $this = this
      this.loading = true;
      this.toggleAllRealEstates(false);
      this.getAllRealEstatesWithCounter({
        page: $this.page.currentPage,
        amount: $this.page.amount,
        filter: $this.filter,
        sortcolumn: 'updatedDate',
        sortby: $this.sort.direction,
      })
          .then(this.onRealEstatesRetrieve, (error) => $this.error = error)


      this.getAllBuildings({
        page: 1,
        amount: 9999,
        filter: $this.buildingFilter,
        sortcolumn: 'updatedDate',
        sortby: 'desc',
      }).then(this.onBuildingsRetrieve, (error) => $this.error = error)


      this.getExcelTemplateUrl().then((url) => $this.excelUrl = url);

    },

    onBuildingsRetrieve(data) {
      this.buildings = data["Buildings"];
      this.buildingsMap = data["Buildings"].toObject("id");
    },

    onRealEstatesRetrieve(data) {
      this.page = {
        amount: data['Amount'],
        total: data['Total'],
        currentPage: data['Page']
      };
      this.sort = {
        by: data["sortColumn"],
        direction: data["sortOrder"]
      }
      this.realEstates = data["RealEstates"];
      this.loadDropZone();

      this.loading = false;
    },
    onRealEstateSelect(id) {
      this.selected = id
      if (id === 0) {
        this.dialogs.realEstate = true
      }
    },
    copyToNextExpenses() {
      let $this = this;
      this.copyToNextExpensesRealEstate()
          .then(() => {
            $this.refresh()
          })
    },
    countDoneItems(todo) {
      if (todo == null) return "0/0 jobs";
      const doneItems = todo.filter((item) => item.done === true);
      const totalItems = todo.length;
      return `${doneItems.length}/${totalItems} jobs`;
    },

    onSort(data) {
      this.sort.by = data.sortBy;
      this.sort.direction = data.sortDesc ? "desc" : "asc";
      this.refresh();
    },
    onRowClick(realEstate) {
      window.open(`/real_estate/edit/${realEstate.id}`, '_blank');
    },
    submitRealEstate() {
      this.$root.$emit('crm::submitRealEstate')
    },
    onSubmitted(server) {
      console.log("RealEstate -> onSubmitted()", server)
      if (server.id != null)
        this.selected = server.id;
      this.refresh()
      this.dialogs.realEstate = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.features-group .btn-group-toggle {
  width: 100%;

  i {
    font-size: 26px;
  }

  .btn {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px dashed #f69c55;
  }
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.3); /* Semi-transparent white background */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000; /* Adjust the z-index as needed */
}

.loading-spinner {
  text-align: center;
}


/*.features-group .btn {
  width: 100%;
  height: 100px;
  text-align: center;
  vertical-align: middle;
  //color:  !important;
}*/

.dropzone-modern {
  border: 2px dashed #007bff;
  padding: 20px;
  border-radius: 10px;
  background: #f0f8ff;
  transition: all 0.3s ease-in-out;
}

.dark-layout .dropzone-modern {
  background: #233189;
}

.dropzone-modern:hover {
  background: #e6f7ff;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
}

.dropzone-custom-title {
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.subtitle {
  font-size: 0.9rem;
  color: #6c757d;
}

.b-list-group-item {
  border: none;
  border-bottom: 1px solid #f1f1f1;
}

.b-list-group-item:last-child {
  border-bottom: none;
}

.shadow-lg {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
}
</style>

<!-- ===== Premium real-estate list (card layout) ===== -->
<style lang="scss" scoped>
.realestates-page {
  .re-list-toolbar {
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 10px; margin-bottom: 14px;
  }
  .re-list-toolbar__left { display: flex; align-items: center; gap: 20px; flex-wrap: wrap; }
  .re-list-toolbar__perpage { display: flex; align-items: center; gap: 4px; }
  .re-selectall { font-weight: 600; font-size: 13px; }
  ::v-deep .re-sort-dd .btn {
    border-radius: 10px; font-weight: 700; font-size: 12.5px;
    border: 1px solid rgba(34, 41, 47, 0.12); background: #fff; color: #6e6b7b;
  }

  .re-bulkbar {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 11px 16px; margin-bottom: 14px; border-radius: 14px;
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.13), rgba(115, 103, 240, 0.05));
    border: 1px solid rgba(115, 103, 240, 0.25);
  }
  .re-bulkbar__count {
    font-size: 13px; color: #6e6b7b; display: flex; align-items: center; gap: 6px;
    b { color: #7367f0; font-size: 15px; }
  }
  .re-bulkbar__actions { display: flex; align-items: center; gap: 8px; }
  .re-bulk-btn { border-radius: 9px !important; font-weight: 700; border: none !important; }
  .re-bulk-btn--danger { background: linear-gradient(135deg, #ea5455, #ff7b7c) !important; color: #fff !important; }
  .re-bulk-btn--ghost { background: transparent !important; color: #6e6b7b !important; border: 1px solid rgba(130, 134, 139, 0.35) !important; }
  ::v-deep .re-bulk-adv .btn { border-radius: 9px !important; font-weight: 700; }

  // ── Property cards ──
  .re-plist { display: flex; flex-direction: column; gap: 9px; }

  // ── Hotel mode: building/hotel group dividers ──
  .re-group { display: flex; flex-direction: column; gap: 9px; }
  .re-group + .re-group { margin-top: 10px; }
  .re-group__head {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 16px; border-radius: 12px; cursor: pointer; user-select: none;
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.10), rgba(115, 103, 240, 0.03));
    border: 1px solid rgba(115, 103, 240, 0.22);
    transition: background 0.15s ease;
    &:hover { background: linear-gradient(135deg, rgba(115, 103, 240, 0.16), rgba(115, 103, 240, 0.06)); }
  }
  .re-group__icon {
    display: inline-flex; align-items: center; justify-content: center;
    width: 30px; height: 30px; border-radius: 9px;
    background: rgba(115, 103, 240, 0.15); color: #7367f0;
    &--none { background: rgba(255, 159, 67, 0.15); color: #ff9f43; }
  }
  .re-group__title { font-weight: 700; font-size: 14.5px; color: #5e5873; }
  .re-group__count { font-weight: 600; }
  .re-group__spacer { flex: 1; }
  .re-group__open { padding: 4px 8px; }
  .re-group__chev { color: #6e6b7b; }

  .re-pcard {
    position: relative; display: flex; align-items: stretch; gap: 15px;
    padding: 12px 16px 12px 22px;
    background: #fff; border: 1px solid rgba(34, 41, 47, 0.08);
    border-radius: 16px;
    box-shadow: 0 1px 2px rgba(34, 41, 47, 0.04), 0 6px 20px rgba(34, 41, 47, 0.05);
    cursor: pointer;
    transition: transform 0.16s ease, box-shadow 0.18s ease, border-color 0.18s ease;
    overflow: hidden;
  }
  .re-pcard:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 30px rgba(34, 41, 47, 0.12);
    border-color: rgba(115, 103, 240, 0.35);
  }
  .re-pcard--selected {
    border-color: #7367f0;
    box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.25), 0 8px 24px rgba(115, 103, 240, 0.15);
  }
  .re-pcard__accent { position: absolute; left: 0; top: 0; bottom: 0; width: 5px; }
  .re-pcard__accent.is-on { background: linear-gradient(180deg, #28c76f, #48da89); }
  .re-pcard__accent.is-off { background: linear-gradient(180deg, #ea5455, #ff7b7c); }

  .re-pcard__lead { display: flex; align-items: center; gap: 12px; }
  .re-pcard__check { margin: 0; }
  .re-pcard__thumb {
    width: 58px; height: 58px; border-radius: 13px; flex-shrink: 0; overflow: hidden;
    display: flex; align-items: center; justify-content: center;
    background: rgba(115, 103, 240, 0.08); color: #7367f0;
    box-shadow: 0 4px 12px rgba(34, 41, 47, 0.12);
    img { width: 100%; height: 100%; object-fit: cover; }
  }

  .re-pcard__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
  .re-pcard__top { display: flex; align-items: center; gap: 8px; }
  .re-pcard__name {
    font-size: 15.5px; font-weight: 800; color: #2f2d3d; letter-spacing: -0.2px;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .re-pcard__idchip {
    font-size: 10px; font-weight: 700; border-radius: 6px; cursor: pointer;
    background: rgba(130, 134, 139, 0.14); color: #6e6b7b;
    display: inline-flex; align-items: center; gap: 3px; flex-shrink: 0;
  }
  .re-pcard__addr {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 13px; font-weight: 600; color: #4b4b5a;
    background: rgba(115, 103, 240, 0.08);
    padding: 4px 11px; border-radius: 9px;
    align-self: flex-start; max-width: 100%;
    svg { color: #7367f0; flex-shrink: 0; }
  }
  .re-pcard__meta { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 2px; }
  .re-pcard__chip--channel { font-weight: 700; }
  .re-pcard__chip {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 11.5px; font-weight: 600; color: #6e6b7b;
    padding: 3px 9px; border-radius: 7px; background: rgba(34, 41, 47, 0.05);
    svg { color: #b9b9c3; }
  }
  .re-pcard__chip--agent { background: rgba(0, 207, 232, 0.10); color: #00a8c4; svg { color: #00a8c4; } }
  .re-pcard__chip--cat { background: rgba(255, 159, 67, 0.13); color: #e07d10; svg { color: #e07d10; } }
  .re-pcard__chip--res { background: rgba(115, 103, 240, 0.10); color: #7367f0; svg { color: #7367f0; } }

  .re-pcard__platforms { display: flex; gap: 5px; margin-top: 5px; }
  .re-plat {
    width: 26px; height: 26px; padding: 0 !important; border: none !important;
    border-radius: 7px !important; color: #fff !important; font-weight: 800; font-size: 12px;
    display: inline-flex; align-items: center; justify-content: center; line-height: 1;
  }

  .re-pcard__side {
    display: flex; flex-direction: column; align-items: flex-end;
    justify-content: space-between; gap: 8px; flex-shrink: 0;
  }
  .re-pcard__badges { display: flex; flex-wrap: wrap; gap: 5px; justify-content: flex-end; max-width: 280px; }
  .re-pcard__pricerow { text-align: right; }
  .re-pcard__price { font-size: 16px; font-weight: 800; color: #7367f0; letter-spacing: -0.3px; }
  .re-pcard__updated {
    display: flex; align-items: center; gap: 4px; justify-content: flex-end;
    font-size: 11px; color: #b9b9c3; margin-top: 2px; svg { color: #b9b9c3; }
  }
  .re-pcard__actions { display: flex; gap: 6px; }
  .re-act {
    width: 34px; height: 34px; padding: 0 !important; border-radius: 10px !important; border: none !important;
    display: inline-flex; align-items: center; justify-content: center;
    transition: transform 0.12s ease;
  }
  .re-act:hover { transform: translateY(-2px); }
  .re-act--reserve { background: rgba(115, 103, 240, 0.12) !important; color: #7367f0 !important; }
  .re-act--disable { background: rgba(234, 84, 85, 0.12) !important; color: #ea5455 !important; }
  .re-act--open { background: rgba(40, 199, 111, 0.12) !important; color: #28c76f !important; }

  // ── Premium empty state ──
  .re-empty {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center; padding: 54px 20px;
  }
  .re-empty__art {
    position: relative; width: 118px; height: 118px; margin-bottom: 16px;
    display: flex; align-items: center; justify-content: center; border-radius: 50%;
    background: radial-gradient(circle at 50% 42%, rgba(115, 103, 240, 0.14), rgba(115, 103, 240, 0) 70%);
    svg { width: 80px; height: 80px; }
  }
  .re-empty__title { font-size: 16px; font-weight: 800; color: #2f2d3d; letter-spacing: -0.2px; }
  .re-empty__sub { font-size: 13px; color: #a5a3b0; margin-top: 5px; max-width: 340px; }
  .re-empty__btn.btn {
    margin-top: 16px; border-radius: 10px !important; font-weight: 700;
    display: inline-flex; align-items: center; gap: 6px;
    background: linear-gradient(135deg, #7367f0, #9e8bff) !important; border: none !important; color: #fff !important;
    box-shadow: 0 6px 16px rgba(115, 103, 240, 0.38);
  }

  // ── Premium filter panel ──
  .re-filterpanel {
    background: #fff; border: 1px solid rgba(34, 41, 47, 0.08); border-radius: 16px;
    box-shadow: 0 1px 2px rgba(34, 41, 47, 0.04), 0 8px 24px rgba(34, 41, 47, 0.05);
    padding: 14px 16px;
  }
  .re-filterpanel__bar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }

  .re-search { position: relative; flex: 1 1 260px; min-width: 220px; display: flex; align-items: center; }
  .re-search__icon { position: absolute; left: 13px; color: #b9b9c3; pointer-events: none; z-index: 2; }
  ::v-deep .re-search__input.form-control {
    height: 44px; border-radius: 12px; padding-left: 40px; padding-right: 36px;
    font-size: 14px; font-weight: 500; border: 1px solid rgba(34, 41, 47, 0.10); background: #f8f8fb;
  }
  ::v-deep .re-search__input.form-control:focus {
    background: #fff; border-color: rgba(115, 103, 240, 0.55); box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
  }
  .re-search__clear {
    position: absolute; right: 12px; color: #b9b9c3; cursor: pointer; z-index: 2; transition: color 0.15s ease;
    &:hover { color: #ea5455; }
  }

  .re-seg {
    display: inline-flex; align-items: center; gap: 3px; padding: 4px;
    background: rgba(34, 41, 47, 0.05); border-radius: 12px;
  }
  .re-seg__btn {
    border: none; background: transparent; cursor: pointer; white-space: nowrap;
    display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: 9px;
    font-size: 12.5px; font-weight: 700; color: #6e6b7b; transition: all 0.15s ease;
  }
  .re-seg__btn:hover { color: #2f2d3d; }
  .re-seg__dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; opacity: 0.5; }
  .re-seg__btn--on { color: #28c76f; }
  .re-seg__btn--off { color: #ea5455; }
  .re-seg__btn.is-active { background: #fff; color: #2f2d3d; box-shadow: 0 2px 6px rgba(34, 41, 47, 0.12); }
  .re-seg__btn--on.is-active { color: #1fa25a; }
  .re-seg__btn--off.is-active { color: #d33a3b; }
  .re-seg__btn.is-active .re-seg__dot { opacity: 1; }

  .re-filterpanel__summary {
    display: inline-flex; align-items: center; gap: 6px; margin-left: auto;
    font-size: 12.5px; color: #6e6b7b; white-space: nowrap;
    svg { color: #7367f0; }
    b { color: #2f2d3d; font-size: 15px; font-weight: 800; }
  }

  .re-filterpanel__quick {
    display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap;
    margin-top: 12px; padding-top: 12px; border-top: 1px solid rgba(34, 41, 47, 0.06);
  }
  .re-qf { flex: 1 1 150px; min-width: 140px; }
  .re-qf__label {
    display: block; font-size: 10.5px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.4px; color: #a5a3b0; margin-bottom: 4px;
  }
  ::v-deep .re-qf__sel .vs__dropdown-toggle {
    border-radius: 10px; min-height: 38px; border-color: rgba(34, 41, 47, 0.12); background: #fff;
  }
  ::v-deep .re-qf__sel.vs--open .vs__dropdown-toggle {
    border-color: rgba(115, 103, 240, 0.55); box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
  }

  .re-qf--actions { flex: 0 0 auto; min-width: 0; display: flex; align-items: center; gap: 8px; margin-left: auto; }
  ::v-deep .re-morebtn.btn, ::v-deep .re-clearbtn.btn {
    display: inline-flex; align-items: center; border-radius: 10px !important;
    font-weight: 700; font-size: 12.5px; padding: 9px 13px;
  }
  ::v-deep .re-morebtn.btn { background: rgba(115, 103, 240, 0.10) !important; color: #7367f0 !important; border: none !important; }
  ::v-deep .re-morebtn.btn.is-open { background: rgba(115, 103, 240, 0.18) !important; }
  ::v-deep .re-morebtn__count.badge { background: #7367f0; color: #fff; margin-left: 6px; border-radius: 6px; font-size: 10px; }
  ::v-deep .re-clearbtn.btn {
    background: transparent !important; color: #ea5455 !important; border: 1px solid rgba(234, 84, 85, 0.35) !important;
  }
  ::v-deep .re-clearbtn.btn:hover { background: rgba(234, 84, 85, 0.08) !important; }
  ::v-deep .re-clearbtn__count.badge { background: #ea5455; color: #fff; margin-left: 6px; border-radius: 6px; font-size: 10px; }

  .re-filterpanel__adv { margin-top: 14px; padding-top: 14px; border-top: 1px dashed rgba(34, 41, 47, 0.12); }
  .re-adv-head {
    display: flex; align-items: center; gap: 6px; margin-bottom: 12px;
    font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; color: #a5a3b0;
    svg { color: #7367f0; }
  }
  .re-adv-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px 12px; }
  .re-field label {
    display: block; font-size: 10.5px; font-weight: 700; text-transform: uppercase;
    letter-spacing: 0.4px; color: #a5a3b0; margin-bottom: 4px;
  }
  ::v-deep .re-field .form-control { border-radius: 10px; height: 38px; border-color: rgba(34, 41, 47, 0.12); }
  ::v-deep .re-field .vs__dropdown-toggle { border-radius: 10px; min-height: 38px; }

  .re-adv-prices { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 14px; }
  .re-priceblock {
    flex: 1 1 220px; min-width: 200px; padding: 11px 13px; border-radius: 12px;
    background: rgba(115, 103, 240, 0.04); border: 1px solid rgba(115, 103, 240, 0.12);
  }
  .re-priceblock__title {
    display: flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 800;
    text-transform: uppercase; letter-spacing: 0.4px; color: #7367f0; margin-bottom: 7px;
  }
  .re-priceblock__row { display: flex; align-items: center; gap: 8px; }
  ::v-deep .re-priceblock__row .form-control { border-radius: 9px; height: 36px; text-align: center; }
  .re-priceblock__dash { color: #b9b9c3; font-weight: 700; }

  // Table pagination premium
  ::v-deep .table-card {
    border: 1px solid rgba(34, 41, 47, 0.07); border-radius: 16px;
    box-shadow: 0 1px 2px rgba(34, 41, 47, 0.04), 0 10px 28px rgba(34, 41, 47, 0.06); overflow: hidden;
  }
  ::v-deep .table-card > .card-header { padding: 16px 20px; border-bottom: 1px solid rgba(34, 41, 47, 0.06); }
  ::v-deep .table-card .card-title { font-weight: 800; font-size: 16px; letter-spacing: -0.2px; }
  ::v-deep .pagination .page-item.active .page-link {
    background: linear-gradient(135deg, #7367f0, #9e8bff); border-color: #7367f0;
    box-shadow: 0 4px 10px rgba(115, 103, 240, 0.4);
  }
}

// ── Dark mode ──
.dark-layout .realestates-page {
  .re-filterpanel, ::v-deep .table-card {
    background: #283046; border-color: rgba(255, 255, 255, 0.07); box-shadow: 0 2px 14px rgba(0, 0, 0, 0.28);
  }
  ::v-deep .re-sort-dd .btn { background: #1f2638; border-color: rgba(255, 255, 255, 0.1); color: #b4b7bd; }
  .re-pcard { background: #283046; border-color: rgba(255, 255, 255, 0.07); box-shadow: 0 2px 14px rgba(0, 0, 0, 0.25); }
  .re-pcard:hover { border-color: rgba(115, 103, 240, 0.4); }
  .re-pcard__name { color: #e6e7ea; }
  .re-pcard__chip, .re-bulkbar__count { color: #b4b7bd; }
  .re-pcard__addr {
    color: #d7d9e0; background: rgba(115, 103, 240, 0.20);
    svg { color: #a99cff; }
  }
  .re-pcard__chip { background: rgba(255, 255, 255, 0.06); }
  .re-pcard__chip--agent { background: rgba(0, 207, 232, 0.16); color: #4fd8ee; svg { color: #4fd8ee; } }
  .re-pcard__chip--cat { background: rgba(255, 159, 67, 0.18); color: #ffb066; svg { color: #ffb066; } }
  .re-pcard__chip--res { background: rgba(115, 103, 240, 0.18); color: #a99cff; svg { color: #a99cff; } }
  .re-pcard__thumb { background: rgba(115, 103, 240, 0.18); }
  .re-pcard__idchip { background: rgba(255, 255, 255, 0.08); color: #b4b7bd; }
  .re-bulkbar { background: rgba(115, 103, 240, 0.16); border-color: rgba(115, 103, 240, 0.3); }

  // filter panel dark
  ::v-deep .re-search__input.form-control { background: #1f2638; border-color: rgba(255, 255, 255, 0.10); color: #d0d2d6; }
  ::v-deep .re-search__input.form-control:focus { background: #161d29; }
  .re-search__icon, .re-search__clear { color: #6e7180; }
  .re-seg { background: rgba(255, 255, 255, 0.06); }
  .re-seg__btn { color: #b4b7bd; }
  .re-seg__btn:hover { color: #fff; }
  .re-seg__btn--on { color: #28c76f; }
  .re-seg__btn--off { color: #ea5455; }
  .re-seg__btn.is-active { background: #161d29; color: #fff; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4); }
  .re-filterpanel__summary { color: #b4b7bd; b { color: #e6e7ea; } }
  .re-filterpanel__quick { border-top-color: rgba(255, 255, 255, 0.07); }
  .re-qf__label, .re-field label, .re-adv-head { color: #9b9aa8; }
  ::v-deep .re-field .form-control { background: #1f2638; border-color: rgba(255, 255, 255, 0.10); color: #d0d2d6; }
  ::v-deep .re-qf__sel .vs__dropdown-toggle { background: #1f2638; border-color: rgba(255, 255, 255, 0.10); }
  .re-filterpanel__adv { border-top-color: rgba(255, 255, 255, 0.12); }
  .re-priceblock { background: rgba(115, 103, 240, 0.10); border-color: rgba(115, 103, 240, 0.22); }
  ::v-deep .re-priceblock__row .form-control { background: #1f2638; border-color: rgba(255, 255, 255, 0.10); color: #d0d2d6; }
  ::v-deep .re-morebtn.btn { background: rgba(115, 103, 240, 0.18) !important; color: #a99cff !important; }
  .re-empty__title { color: #e6e7ea; }
  .re-empty__sub { color: #6e7180; }
  .re-group__head {
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.16), rgba(115, 103, 240, 0.05));
    border-color: rgba(115, 103, 240, 0.30);
  }
  .re-group__title { color: #e6e7ea; }
  .re-group__chev { color: #b4b7bd; }
}
</style>
